// Comprehensive PWA Installation Test Suite

export const testPWAInstallationComprehensive = () => {
  console.log('🧪 === COMPREHENSIVE PWA INSTALLATION TEST ===');
  
  // Test 1: Basic Requirements
  const basicRequirements = {
    hasServiceWorker: 'serviceWorker' in navigator,
    hasManifest: document.querySelector('link[rel="manifest"]') !== null,
    hasHTTPS: window.location.protocol === 'https:' || window.location.hostname === 'localhost',
    hasPushManager: 'PushManager' in window,
    hasIndexedDB: 'indexedDB' in window,
    hasLocalStorage: 'localStorage' in window
  };
  
  console.log('📋 Basic Requirements:', basicRequirements);
  
  // Test 2: Browser Support
  const userAgent = navigator.userAgent;
  const browserSupport = {
    isChrome: /Chrome/.test(userAgent) && !/(FxiOS|OPR|Edg|Edge|EdgA|EdgiOS)/i.test(userAgent),
    isFirefox: /Firefox|FxiOS/.test(userAgent),
    isSafari: /^((?!Chrome|Android).)*Safari/i.test(userAgent),
    isEdge: /Edg|Edge|EdgA|EdgiOS/i.test(userAgent),
    isOpera: /OPR|Opera/.test(userAgent),
    isSupported: /Chrome|Firefox|Safari|Edge|Opera/i.test(userAgent)
  };
  
  console.log('🌐 Browser Support:', browserSupport);
  
  // Test 3: Device Detection
  const deviceInfo = {
    isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent),
    isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    isIOS: /iPad|iPhone|iPod/.test(userAgent),
    isAndroid: /Android/.test(userAgent),
    isDesktop: !/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent),
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight
  };
  
  console.log('📱 Device Info:', deviceInfo);
  
  // Test 4: PWA Mode Detection
  const pwaMode = {
    isStandalone: window.matchMedia('(display-mode: standalone)').matches,
    isNavigatorStandalone: (window.navigator as any).standalone === true,
    isFullscreen: window.matchMedia('(display-mode: fullscreen)').matches,
    isMinimalUI: window.matchMedia('(display-mode: minimal-ui)').matches,
    isPWAMode: window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true
  };
  
  console.log('🏠 PWA Mode:', pwaMode);
  
  // Test 5: Install Prompt Status
  const installStatus = {
    hasDeferredPrompt: !!(window as any).deferredPrompt,
    hasUserGesture: !!(window as any).userGesture,
    canInstall: !!(window as any).deferredPrompt && !!(window as any).userGesture,
    isInstalled: pwaMode.isPWAMode
  };
  
  console.log('🚀 Install Status:', installStatus);
  
  // Test 6: Manifest Analysis
  const manifestTest = async () => {
    try {
      const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
      if (manifestLink) {
        const response = await fetch(manifestLink.href);
        const manifest = await response.json();
        
        console.log('📄 Manifest Analysis:', {
          name: manifest.name,
          short_name: manifest.short_name,
          start_url: manifest.start_url,
          display: manifest.display,
          icons: manifest.icons?.length || 0,
          hasRequiredIcons: (manifest.icons || []).some((icon: any) => 
            icon.sizes === '192x192' || icon.sizes === '512x512'
          ),
          theme_color: manifest.theme_color,
          background_color: manifest.background_color
        });
        
        return manifest;
      }
    } catch (error) {
      console.error('❌ Manifest fetch error:', error);
    }
  };
  
  // Test 7: Service Worker Status
  const serviceWorkerTest = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        console.log('⚙️ Service Worker Status:', {
          registrationsCount: registrations.length,
          registrations: registrations.map(reg => ({
            scope: reg.scope,
            state: reg.active?.state,
            scriptURL: reg.active?.scriptURL
          })),
          controller: !!navigator.serviceWorker.controller
        });
      } catch (error) {
        console.error('❌ Service Worker error:', error);
      }
    }
  };
  
  // Test 8: User Engagement Test
  const userEngagementTest = () => {
    const events = ['click', 'scroll', 'keydown', 'touchstart', 'mousedown'];
    let engagementDetected = false;
    
    const testEngagement = () => {
      if (!engagementDetected) {
        engagementDetected = true;
        console.log('👆 User engagement detected!');
        (window as any).userGesture = true;
        
        // Check if install prompt becomes available
        setTimeout(() => {
          if ((window as any).deferredPrompt) {
            console.log('🔔 Install prompt now available after engagement');
          }
        }, 1000);
      }
    };
    
    events.forEach(event => {
      document.addEventListener(event, testEngagement, { once: true, passive: true });
    });
    
    console.log('👆 User Engagement Test: Listening for user interactions...');
  };
  
  // Test 9: Installation Methods Test
  const installationMethodsTest = () => {
    const methods = {
      nativePrompt: !!(window as any).deferredPrompt,
      beforeInstallPrompt: 'onbeforeinstallprompt' in window,
      appInstalled: 'onappinstalled' in window,
      canPrompt: () => {
        if ((window as any).deferredPrompt) {
          return true;
        }
        return false;
      },
      triggerPrompt: async () => {
        if ((window as any).deferredPrompt) {
          try {
            await (window as any).deferredPrompt.prompt();
            const { outcome } = await (window as any).deferredPrompt.userChoice;
            return { success: true, outcome };
          } catch (error) {
            return { success: false, error: error.message };
          }
        }
        return { success: false, error: 'No deferred prompt available' };
      }
    };
    
    console.log('🔧 Installation Methods:', methods);
    return methods;
  };
  
  // Test 10: Overall Assessment
  const overallAssessment = () => {
    const canInstallPWA = basicRequirements.hasServiceWorker && 
                          basicRequirements.hasManifest && 
                          basicRequirements.hasHTTPS && 
                          browserSupport.isSupported &&
                          !pwaMode.isPWAMode;
    
    const issues = [];
    if (!basicRequirements.hasServiceWorker) issues.push('No Service Worker');
    if (!basicRequirements.hasManifest) issues.push('No Manifest');
    if (!basicRequirements.hasHTTPS) issues.push('No HTTPS');
    if (!browserSupport.isSupported) issues.push('Unsupported Browser');
    if (pwaMode.isPWAMode) issues.push('Already Installed');
    
    console.log('📊 Overall Assessment:', {
      canInstallPWA,
      issues,
      recommendation: canInstallPWA ? 'Ready to install' : `Issues: ${issues.join(', ')}`
    });
    
    return { canInstallPWA, issues, recommendation: canInstallPWA ? 'Ready to install' : `Issues: ${issues.join(', ')}` };
  };
  
  // Run all tests
  const runAllTests = async () => {
    console.log('🚀 Running all PWA installation tests...');
    
    // Run async tests
    await manifestTest();
    await serviceWorkerTest();
    
    // Run sync tests
    userEngagementTest();
    const methods = installationMethodsTest();
    const assessment = overallAssessment();
    
    console.log('✅ All tests completed!');
    
    return {
      basicRequirements,
      browserSupport,
      deviceInfo,
      pwaMode,
      installStatus,
      methods,
      assessment
    };
  };
  
  // Make functions available globally
  if (typeof window !== 'undefined') {
    (window as any).testPWAInstallationComprehensive = runAllTests;
    (window as any).testPWAInstallationMethods = installationMethodsTest;
    (window as any).testPWAUserEngagement = userEngagementTest;
  }
  
  return runAllTests();
};

// Quick test function
export const quickPWATest = () => {
  console.log('⚡ Quick PWA Test');
  
  const quick = {
    canInstall: !!(window as any).deferredPrompt,
    isInstalled: window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true,
    hasUserGesture: !!(window as any).userGesture,
    hasServiceWorker: 'serviceWorker' in navigator,
    hasManifest: document.querySelector('link[rel="manifest"]') !== null,
    hasHTTPS: window.location.protocol === 'https:' || window.location.hostname === 'localhost'
  };
  
  console.log('Quick Results:', quick);
  return quick;
};

// Make functions available globally
if (typeof window !== 'undefined') {
  (window as any).testPWAInstallationComprehensive = testPWAInstallationComprehensive;
  (window as any).quickPWATest = quickPWATest;
}
